<template>
  <div class="data-edit">
    <h4>Editar Bloco de Dados</h4>
    
    <div class="form-group">
      <label>Título:</label>
      <input
        v-model="localBlock.data.title"
        @input="onChange"
        type="text"
        placeholder="Título do bloco de dados"
      >
    </div>

    <div class="form-group">
      <label>Chave dos Dados:</label>
      <select v-model="localBlock.data.dataKey" @change="onDataKeyChange">
        <option value="dados-gerais">Dados Gerais</option>
        <option value="dados-ambiente">Dados do Ambiente</option>
        <option value="dados-avaliacao">Dados da Avaliação</option>
        <option value="custom">Personalizado</option>
      </select>
    </div>

    <div class="fields-section" v-if="localBlock.data.dataKey === 'custom'">
      <div class="section-header">
        <label>Campos Personalizados:</label>
        <button @click="addField" type="button" class="btn-add">+</button>
      </div>
      
      <div
        v-for="(field, index) in localBlock.data.fields"
        :key="index"
        class="field-item"
      >
        <input
          v-model="field.label"
          @input="onChange"
          placeholder="Rótulo do campo"
          class="field-label"
        >
        <input
          v-model="field.value"
          @input="onChange"
          placeholder="Valor do campo"
          class="field-value"
        >
        <button
          @click="removeField(index)"
          type="button"
          class="btn-remove"
        >
          ×
        </button>
      </div>
    </div>

    <div class="form-group">
      <label>Cor de Fundo:</label>
      <input
        type="color"
        v-model="localBlock.style.backgroundColor"
        @change="onChange"
      >
    </div>

    <div class="form-group">
      <label>Padding:</label>
      <select v-model="localBlock.style.padding" @change="onChange">
        <option value="10px">Pequeno (10px)</option>
        <option value="15px">Normal (15px)</option>
        <option value="20px">Grande (20px)</option>
        <option value="25px">Muito Grande (25px)</option>
      </select>
    </div>

    <div class="form-group">
      <label>Borda:</label>
      <select v-model="localBlock.style.border" @change="onChange">
        <option value="none">Sem Borda</option>
        <option value="1px solid #e9ecef">Borda Leve</option>
        <option value="2px solid #dee2e6">Borda Média</option>
        <option value="3px solid #007bff">Borda Destacada</option>
      </select>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'

interface Props {
  block: any
}

const props = defineProps<Props>()
const emit = defineEmits<{
  update: [block: any]
}>()

const localBlock = ref(JSON.parse(JSON.stringify(props.block)))

// Inicializar dados se não existirem
if (!localBlock.value.data) {
  localBlock.value.data = {
    title: 'Dados',
    dataKey: 'dados-gerais',
    fields: []
  }
}

if (!localBlock.value.style) {
  localBlock.value.style = {
    backgroundColor: '#f8f9fa',
    padding: '15px',
    border: '1px solid #e9ecef',
    borderRadius: '8px'
  }
}

watch(() => props.block, (newBlock) => {
  localBlock.value = JSON.parse(JSON.stringify(newBlock))
}, { deep: true })

function onChange() {
  emit('update', localBlock.value)
}

function onDataKeyChange() {
  // Carregar campos padrão baseado na chave selecionada
  const fieldsMap: any = {
    'dados-gerais': [
      { label: 'Local', value: '' },
      { label: 'Data', value: '' },
      { label: 'Avaliador', value: '' }
    ],
    'dados-ambiente': [
      { label: 'Temperatura', value: '' },
      { label: 'Umidade', value: '' },
      { label: 'Iluminância', value: '' }
    ],
    'dados-avaliacao': [
      { label: 'Tipo de Avaliação', value: '' },
      { label: 'Método', value: '' },
      { label: 'Equipamento', value: '' }
    ]
  }

  if (localBlock.value.data.dataKey !== 'custom') {
    localBlock.value.data.fields = fieldsMap[localBlock.value.data.dataKey] || []
  }
  
  onChange()
}

function addField() {
  if (!localBlock.value.data.fields) {
    localBlock.value.data.fields = []
  }
  localBlock.value.data.fields.push({ label: '', value: '' })
  onChange()
}

function removeField(index: number) {
  localBlock.value.data.fields.splice(index, 1)
  onChange()
}
</script>

<style scoped>
.data-edit {
  padding: 10px 0;
}

h4 {
  margin: 0 0 15px 0;
  color: #333;
  border-bottom: 1px solid #eee;
  padding-bottom: 8px;
}

.form-group {
  margin-bottom: 15px;
}

label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
  color: #555;
  font-size: 13px;
}

input[type="text"], select, input[type="color"] {
  width: 100%;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 6px 8px;
  
  &:focus {
    outline: none;
    border-color: #007bff;
  }
}

.fields-section {
  border: 1px solid #eee;
  border-radius: 4px;
  padding: 10px;
  margin-bottom: 15px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.btn-add {
  background: #28a745;
  color: white;
  border: none;
  border-radius: 4px;
  width: 24px;
  height: 24px;
  cursor: pointer;
  font-size: 14px;
  
  &:hover {
    background: #218838;
  }
}

.field-item {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
  align-items: center;
}

.field-label, .field-value {
  flex: 1;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 4px 6px;
  font-size: 12px;
  
  &:focus {
    outline: none;
    border-color: #007bff;
  }
}

.btn-remove {
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  width: 20px;
  height: 20px;
  cursor: pointer;
  font-size: 12px;
  
  &:hover {
    background: #c82333;
  }
}
</style>